/**
 * Centralized Firestore Client
 *
 * Single source of truth for the admin Firestore connection.
 * Handles database selection and emulator connection consistently.
 *
 * Database: always `lasoxp-agent` (matches backend config).
 * Emulator: connected synchronously at module load when VITE_USE_FIREBASE_EMULATOR=true.
 */

import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { app } from '../config/firebase';
import { devLog } from '../utils/dev-log';

function isUsingEmulator(): boolean {
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') return true;
  if (import.meta.env.DEV && typeof window !== 'undefined' && window.location.hostname === 'localhost') return true;
  return false;
}

function initFirestore(): Firestore {
  const emulator = isUsingEmulator();

  // Emulator: (default) database — Firebase CLI doesn't support named DB export/import
  // Production: lasoxp-agent named database
  const firestore = emulator
    ? getFirestore(app)
    : getFirestore(app, 'lasoxp-agent');

  if (emulator) {
    try {
      connectFirestoreEmulator(firestore, 'localhost', 8081);
    } catch {
      // Already connected (hot-reload) — ignore
    }
  }

  const dbName = emulator ? '(default)' : 'lasoxp-agent';
  const env = emulator ? 'EMULATOR' : 'CLOUD';
  devLog(`[Firestore] database: ${dbName} [${env}]`);

  return firestore;
}

/** Singleton Firestore instance — use this everywhere in the admin app. */
export const db = initFirestore();
